/**
 * Fly fishing scoring and species targeting utilities
 *
 * Handles trace accuracy → tier mapping, bonus calculation,
 * and species pool weighting based on fly patterns + cast accuracy.
 */

import { FishRarity, RarityWeights, type FlyCastTier, type FlyCastBonus, type FlyPatternDefinition } from '@/types';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';
import { weightedRandom } from './random/weightedRandom';

// ============================================================
// Rarity ordering (for minRarity filtering)
// ============================================================

const RARITY_ORDER: Record<FishRarity, number> = {
  [FishRarity.COMMON]: 0,
  [FishRarity.UNCOMMON]: 1,
  [FishRarity.RARE]: 2,
  [FishRarity.EPIC]: 3,
  [FishRarity.LEGENDARY]: 4,
};

// ============================================================
// Scoring
// ============================================================

/**
 * Map trace accuracy (0-1) to a cast tier
 */
export function getFlyCastTier(accuracy: number): FlyCastTier {
  if (accuracy >= BALANCE_CONFIG.FLY_CAST_PERFECT_THRESHOLD) return 'perfect';
  if (accuracy >= BALANCE_CONFIG.FLY_CAST_GOOD_THRESHOLD) return 'good';
  return 'miss';
}

/**
 * Get the fly cast bonus values for a given tier
 */
export function getFlyCastBonusForTier(tier: FlyCastTier, accuracy: number, flyPatternId?: string): FlyCastBonus {
  switch (tier) {
    case 'perfect':
      return {
        catchBonus: BALANCE_CONFIG.FLY_CAST_PERFECT_CATCH_BONUS,
        qualityBonus: BALANCE_CONFIG.FLY_CAST_PERFECT_QUALITY_BONUS,
        castTier: 'perfect',
        accuracy,
        flyPatternId,
      };
    case 'good':
      return {
        catchBonus: BALANCE_CONFIG.FLY_CAST_GOOD_CATCH_BONUS,
        qualityBonus: BALANCE_CONFIG.FLY_CAST_GOOD_QUALITY_BONUS,
        castTier: 'good',
        accuracy,
        flyPatternId,
      };
    case 'miss':
    default:
      return {
        catchBonus: BALANCE_CONFIG.FLY_CAST_MISS_CATCH_BONUS,
        qualityBonus: BALANCE_CONFIG.FLY_CAST_MISS_QUALITY_BONUS,
        castTier: 'miss',
        accuracy,
        flyPatternId,
      };
  }
}

/**
 * Determine whether a fly should be consumed (lost) after a cast.
 * Good casts preserve flies; bad casts risk losing them.
 * @returns true if the fly should be consumed (lost)
 */
export function shouldConsumeFly(accuracy: number): boolean {
  if (accuracy >= BALANCE_CONFIG.FLY_CAST_GOOD_THRESHOLD) {
    // 60%+ accuracy: fly is preserved
    return false;
  }
  // Below 60% accuracy: 50% chance to lose the fly
  return Math.random() < BALANCE_CONFIG.FLY_LOSS_CHANCE_ON_MISS;
}

// ============================================================
// Species targeting
// ============================================================

/**
 * Roll a fish species using fly pattern targeting and cast accuracy rarity boost.
 *
 * 1. If flyPattern.minRarity is set, filter out species below that rarity
 * 2. Multiply target species weights by flyPattern.targetWeightMultiplier
 * 3. On Perfect/Good, multiply rare+ species weights by rarity boost
 * 4. Weighted random selection
 *
 * @param fishPool - Array of fish definition IDs in the current zone
 * @param fishDefinitions - Map of fish ID → { rarity: FishRarity } (minimal interface)
 * @param flyPattern - The active fly pattern (or null for no pattern)
 * @param castTier - The fly cast accuracy tier
 * @returns Selected fish definition ID
 */
export function rollFlyFishSpecies(
  fishPool: string[],
  fishDefinitions: Record<string, { rarity: FishRarity }>,
  flyPattern: FlyPatternDefinition | null,
  castTier: FlyCastTier,
): string {
  if (fishPool.length === 0) {
    throw new Error('rollFlyFishSpecies: empty fish pool');
  }

  // Step 1: Filter pool by minimum rarity (if fly pattern specifies one)
  let effectivePool = fishPool;
  if (flyPattern?.minRarity) {
    const minRarityOrder = RARITY_ORDER[flyPattern.minRarity];
    const filtered = fishPool.filter((id) => {
      const def = fishDefinitions[id];
      if (!def) return false;
      return RARITY_ORDER[def.rarity] >= minRarityOrder;
    });
    // Only use filtered pool if it has species; otherwise fall back to full pool
    if (filtered.length > 0) {
      effectivePool = filtered;
    }
  }

  // Step 2: Build weighted entries
  const rarityBoost =
    castTier === 'perfect'
      ? BALANCE_CONFIG.FLY_CAST_PERFECT_RARITY_BOOST
      : castTier === 'good'
        ? BALANCE_CONFIG.FLY_CAST_GOOD_RARITY_BOOST
        : 1.0;

  const entries = effectivePool.map((id) => {
    const def = fishDefinitions[id];
    if (!def) return { id, weight: 0.01 };

    let weight = RarityWeights[def.rarity] ?? 0.01;

    // Fly pattern species targeting boost
    if (flyPattern?.targetSpecies.includes(id)) {
      weight *= flyPattern.targetWeightMultiplier;
    }

    // Cast accuracy rarity boost for rare+ species
    if (
      rarityBoost > 1.0 &&
      (def.rarity === FishRarity.RARE ||
        def.rarity === FishRarity.EPIC ||
        def.rarity === FishRarity.LEGENDARY)
    ) {
      weight *= rarityBoost;
    }

    return { id, weight };
  });

  return weightedRandom(entries);
}
